import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, EMPTY, Observable, Subject, throwError } from 'rxjs';
import { getReplyUsername } from 'src/app/common/app.helpers';
import { CommentPositionNotFoundError } from 'src/app/common/exceptions/comment-position-not-found';
import { SortingParams } from 'src/app/interfaces/connections.interface';
import {
  AddCommentContent,
  CommentPosition,
  CommentView,
  FullRootCommentView,
  LoadedCommentsState,
  makeDefaultLoadedCommentsState,
} from 'src/app/interfaces/posts.interface';
import {
  CommentPosition as AbsoluteCommentPosition,
  ListOfCommentReplies,
  ListOfRootComments,
  ReplyComment,
  RootComment,
} from '../../../../swagger-api';
import { CommentsContentProviderService } from './comments.content-provider.service';
import { CommentsImporter } from './comments.importer.helper';

export abstract class CommentsBaseService {
  public static readonly COMMENTS_PER_PAGE: number = 5;
  public static readonly COMMENT_REPLIES_PER_PAGE: number = 2;

  protected commentsSortingStrategy: SortingParams = SortingParams.CreatedDesc;
  protected replySortingStrategy: SortingParams = SortingParams.CreatedAsc;
  protected canViewComments = true;

  private loadedComments: LoadedCommentsState = makeDefaultLoadedCommentsState();
  private commentsChanges$: Subject<LoadedCommentsState> = new Subject();

  protected commentsImporter = inject(CommentsImporter);

  public get commentsChanges(): Observable<LoadedCommentsState> {
    return this.commentsChanges$.asObservable();
  }

  public changeSortingStrategy(sortComments: SortingParams, sortReplies: SortingParams): void {
    this.commentsSortingStrategy = sortComments;
    this.replySortingStrategy = sortReplies;

    this.commentsImporter.changeCommentsSortingStrategy(sortComments);
    this.commentsImporter.changeReplySortingStrategy(sortReplies);

    this.loadInitialComments();
  }

  public getCommentPosition(commentId: string): Observable<AbsoluteCommentPosition> {
    return this.contentProvider()
      .queryCommentPosition(commentId)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => [new CommentPositionNotFoundError(error.message, commentId)]);
        }),
      );
  }

  public loadInitialComments(commentLimit?: number, replyLimit?: number): void {
    if (!this.canViewComments) {
      return;
    }

    this.contentProvider()
      .queryInitialComments(commentLimit, replyLimit)
      .pipe(catchError(() => EMPTY))
      .subscribe((rootComments: ListOfRootComments) => {
        this.loadedComments = this.commentsImporter.importComments(rootComments);
        this.commentsChanges$.next(this.loadedComments);
      });
  }

  public loadMoreRootComments(): void {
    this.contentProvider()
      .queryMoreRootComments(this.loadedComments.loadedRootCommentsCount)
      .pipe(catchError(() => EMPTY))
      .subscribe((rootComments: ListOfRootComments) => this.mergeExtraRootComments(rootComments));
  }

  public loadAllRemainingRootComments(): void {
    this.contentProvider()
      .queryRemainingRootComments(this.loadedComments.loadedRootCommentsCount)
      .pipe(catchError(() => EMPTY))
      .subscribe((rootComments: ListOfRootComments) => this.mergeExtraRootComments(rootComments));
  }

  public loadMoreReplies(rootCommentPosition: CommentPosition): void {
    const fullRootCommentView: FullRootCommentView =
      this.loadedComments.rootCommentFullViews[rootCommentPosition.indexRoot];
    const rootCommentId: string = fullRootCommentView.rootCommentView.source.entity_id;

    this.contentProvider()
      .queryMoreReplyComments(rootCommentId, fullRootCommentView.loadedReplyCommentsCount)
      .pipe(catchError(() => EMPTY))
      .subscribe((replies: ListOfCommentReplies) => {
        this.mergeExtraReplyComments(rootCommentPosition, replies);
      });
  }

  public loadAllRemainingReplies(rootCommentPosition: CommentPosition): void {
    const fullRootCommentView: FullRootCommentView =
      this.loadedComments.rootCommentFullViews[rootCommentPosition.indexRoot];
    const rootCommentId: string = fullRootCommentView.rootCommentView.source.entity_id;

    this.contentProvider()
      .queryRemainingReplyComments(rootCommentId, fullRootCommentView.loadedReplyCommentsCount)
      .pipe(catchError(() => EMPTY))
      .subscribe((replies: ListOfCommentReplies) => {
        this.mergeExtraReplyComments(rootCommentPosition, replies);
      });
  }

  /**
   * @description Processes the comment text and call appropriate method for adding
   *              the new comment.
   * @param {string} text The comment text.
   */
  public addOrReplyComment(info: AddCommentContent): void {
    const repliedCommentPosition: CommentPosition | null = info.repliedCommentPosition
      ? info.repliedCommentPosition
      : this.findLastCommentBy(getReplyUsername(info.text));

    if (repliedCommentPosition) {
      const repliedComment: CommentView = this.resolveCommentByPosition(repliedCommentPosition);
      this.replyComment(info.text.trim(), repliedComment, repliedCommentPosition);
      return;
    }

    this.addComment(info.text);
  }

  /**
   * @description Shows the modal with 'Are you sure you want to remove the comment's
   *              description and removes the comment if the action was confirmed.
   */
  public removeComment(commentPosition: CommentPosition): void {
    const removedComment: CommentView = this.resolveCommentByPosition(commentPosition);
    this.contentProvider()
      .executeDeleteComment(removedComment.source.entity_id)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.updateLoadedComments(commentPosition);
      });
  }

  public resolveCommentByPosition(commentPosition: CommentPosition): CommentView {
    const rootComment: FullRootCommentView = this.loadedComments.rootCommentFullViews[commentPosition.indexRoot];
    if ('indexReply' in commentPosition) {
      return rootComment.replyCommentViews[commentPosition.indexReply];
    } else {
      return rootComment.rootCommentView;
    }
  }

  protected abstract contentProvider(): CommentsContentProviderService;

  /**
   * @description Adds new comment to the comments list.
   * @param {string} text The comment text.
   */
  private addComment(text: string): void {
    this.contentProvider()
      .executeCreateComment(text)
      .pipe(catchError(() => EMPTY))
      .subscribe((comment: RootComment) => {
        const commentView: CommentView = {
          source: comment,
          parentCommentId: null,
        };

        this.loadedComments = this.commentsImporter.addNewRootComment(this.loadedComments, {
          rootCommentView: commentView,
          replyCommentViews: [],
          loadedReplyCommentsCount: 0,
          totalReplyCommentsCount: 0,
        });

        this.commentsChanges$.next(this.loadedComments);
      });
  }

  private replyComment(text: string, repliedComment: CommentView, repliedCommentPosition: CommentPosition): void {
    const parentCommentId: string = repliedComment.parentCommentId
      ? repliedComment.parentCommentId
      : repliedComment.source.entity_id;

    const repliedCommentId: string | undefined =
      'indexReply' in repliedCommentPosition ? repliedComment.source.entity_id : undefined;

    this.contentProvider()
      .executeReplyComment(parentCommentId, text, repliedCommentId)
      .pipe(catchError(() => EMPTY))
      .subscribe((replyComment: ReplyComment) =>
        this.notifyReplyComment(replyComment, parentCommentId, repliedComment, repliedCommentPosition),
      );
  }

  private updateLoadedComments(commentPosition: CommentPosition): void {
    if ('indexReply' in commentPosition) {
      const parentCommentView: FullRootCommentView =
        this.loadedComments.rootCommentFullViews[commentPosition.indexRoot];
      parentCommentView.replyCommentViews.splice(commentPosition.indexReply, 1);
      parentCommentView.totalReplyCommentsCount--;
      if (commentPosition.indexReply < parentCommentView.loadedReplyCommentsCount) {
        parentCommentView.loadedReplyCommentsCount--;
      }
    } else {
      this.loadedComments.rootCommentFullViews.splice(commentPosition.indexRoot, 1);
      this.loadedComments.totalRootCommentsCount--;
      if (commentPosition.indexRoot < this.loadedComments.loadedRootCommentsCount) {
        this.loadedComments.loadedRootCommentsCount--;
      }
    }

    this.loadedComments = { ...this.loadedComments };
    this.commentsChanges$.next(this.loadedComments);
  }

  private notifyReplyComment(
    replyComment: ReplyComment,
    parentCommentId: string,
    repliedComment: CommentView,
    repliedCommentPosition: CommentPosition,
  ): void {
    const replyCommentView: CommentView = {
      source: replyComment,
      repliedUser: repliedComment.source.creator,
      parentCommentId,
    };

    this.loadedComments = this.commentsImporter.addNewReply(
      this.loadedComments,
      repliedCommentPosition,
      replyCommentView,
    );

    this.commentsChanges$.next(this.loadedComments);
  }

  private findLastCommentBy(userName: string): CommentPosition | null {
    for (let i: number = this.loadedComments.rootCommentFullViews.length - 1; i >= 0; i--) {
      const rootComment: FullRootCommentView = this.loadedComments.rootCommentFullViews[i];
      for (let k: number = rootComment.replyCommentViews.length - 1; k >= 0; k--) {
        const replyComment: CommentView = rootComment.replyCommentViews[k];
        if (replyComment.source.creator.displayed_name === userName) {
          return {
            indexRoot: i,
            indexReply: k,
          };
        }
      }
      if (rootComment.rootCommentView.source.creator.displayed_name === userName) {
        return {
          indexRoot: i,
        };
      }
    }
    return null;
  }

  private mergeExtraRootComments(rootComments: ListOfRootComments): void {
    const moreLoadedComments: LoadedCommentsState = this.commentsImporter.importComments(rootComments);
    this.loadedComments = this.commentsImporter.mergeLoadedStates(this.loadedComments, moreLoadedComments);
    this.commentsChanges$.next(this.loadedComments);
  }

  private mergeExtraReplyComments(rootCommentPosition: CommentPosition, replies: ListOfCommentReplies): void {
    const fullRootCommentView: FullRootCommentView =
      this.loadedComments.rootCommentFullViews[rootCommentPosition.indexRoot];

    const newFullRootCommentView: FullRootCommentView = this.commentsImporter.mergeReplyComments(
      fullRootCommentView,
      replies,
    );

    this.loadedComments.rootCommentFullViews.splice(rootCommentPosition.indexRoot, 1, newFullRootCommentView);

    this.loadedComments = { ...this.loadedComments };
    this.commentsChanges$.next(this.loadedComments);
  }
}
